// binding-rehash-and-cleanup.ts
//
// Binding is controller-owned (§6.2, D06/P1): after ALL subprocesses of an
// attempt are reaped (`groupReaped` from session-runner.ts's kill()), the
// controller re-reads `autopilot-binding.json`, compares its checksum
// against the hash it recorded when writing the file (autopilot-binding-
// file-writer.ts's `checksum`), and only then deletes it.
//
// Hard rule: NEVER delete before `groupReaped` — a still-alive worker's
// hook could be reading the file (test 28). A hash mismatch or missing file
// at that point is a `contract_error` and the file (or its absence) is left
// exactly as found for attempt-forensics.ts to archive — cleanup never
// "fixes" evidence of tampering.
import { promises as fs } from "node:fs";
import { canonicalJson, sha256Checksum } from "./canonical-json-and-checksum.js";

export interface RehashAndCleanupInput {
  missionWorkspace: string;
  bindingPath: string;
  /** Checksum recorded by session-runner.ts / autopilot-binding-file-writer.ts at write time. */
  bindingHash: string;
  groupReaped: boolean;
}

export type RehashAndCleanupStatus = "pending" | "removed" | "contract_error";

export interface RehashAndCleanupResult {
  status: RehashAndCleanupStatus;
  reason?: string;
}

/**
 * Re-hash the binding file against its recorded checksum and delete it only
 * once every subprocess of the attempt has been reaped AND the hash still
 * matches. Returns `pending` (no action taken) while `groupReaped` is false.
 */
export async function rehashAndCleanupBinding(
  input: RehashAndCleanupInput,
): Promise<RehashAndCleanupResult> {
  if (!input.groupReaped) {
    return { status: "pending", reason: "group_not_reaped_yet" };
  }

  let raw: string;
  try {
    raw = await fs.readFile(input.bindingPath, "utf8");
  } catch {
    return {
      status: "contract_error",
      reason:
        "binding_file_missing_at_attempt_end: file bị xoá hoặc chưa từng tồn tại khi verifier đọc lại.",
    };
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return {
      status: "contract_error",
      reason: `binding_file_unparsable: ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  const { checksum, ...body } = parsed as { checksum?: string } & Record<string, unknown>;
  const recomputed = sha256Checksum(canonicalJson(body));
  if (checksum !== recomputed || checksum !== input.bindingHash) {
    return {
      status: "contract_error",
      reason: "binding_hash_mismatch_at_attempt_end: nội dung binding đã bị đổi so với lúc ghi.",
    };
  }

  await fs.rm(input.bindingPath, { force: true });
  return { status: "removed" };
}

/**
 * Best-effort removal of an attempt binding whose verifier never ran (session
 * outcome != ok). Refuses while `groupReaped === false` — a live grandchild
 * hook may still be reading the file (same rule as `rehashAndCleanupBinding`).
 * Dogfood Bug E: a leftover binding was swept into the next run's bootstrap commit.
 */
export async function removeBindingAfterReap(
  bindingPath: string | undefined,
  groupReaped: boolean | undefined,
): Promise<boolean> {
  if (bindingPath === undefined || groupReaped === false) return false;
  try {
    await fs.rm(bindingPath, { force: true });
    return true;
  } catch {
    return false;
  }
}
